import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { VehicleGameEngine } from '../games/core/VehicleGameEngine';
import { Bus } from '../games/vehicles/Bus';
import { Car } from '../games/vehicles/Car';
import { Tractor } from '../games/vehicles/Tractor';
import { Forklift } from '../games/vehicles/Forklift';
import { MonsterTruck } from '../games/vehicles/MonsterTruck';
import { PickupTruck } from '../games/vehicles/PickupTruck';
import { PoliceCar } from '../games/vehicles/PoliceCar';
import { FireTruck } from '../games/vehicles/FireTruck';
import { Ambulance } from '../games/vehicles/Ambulance';
import { GarbageTruck } from '../games/vehicles/GarbageTruck';
import { Bulldozer } from '../games/vehicles/Bulldozer';
import { RoadRoller } from '../games/vehicles/RoadRoller';
import { Excavator } from '../games/vehicles/Excavator';
import { Tank } from '../games/vehicles/Tank';
import GameControls from './GameControls';

interface GameProps {
  gameId: string;
  onBack: () => void;
  speedMultiplier?: number;
}

const Game: React.FC<GameProps> = ({ gameId, onBack }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const engineRef = useRef<VehicleGameEngine | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    if (!rendererRef.current) {
      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setSize(window.innerWidth, window.innerHeight);
      containerRef.current.appendChild(renderer.domElement);
      rendererRef.current = renderer;
    }

    const initGame = async () => {
      let vehicle;
      switch (gameId) {
        case 'bus':
          vehicle = new Bus();
          break;
        case 'tractor':
          vehicle = new Tractor();
          break;
        case 'forklift':
          vehicle = new Forklift();
          break;
        // Thêm các phương tiện mới
        case 'monstertruck':
          vehicle = new MonsterTruck();
          break;
        case 'pickuptruck':
          vehicle = new PickupTruck();
          break;
        case 'policecar':
          vehicle = new PoliceCar();
          break;
        case 'firetruck':
          vehicle = new FireTruck();
          break;
        case 'ambulance':
          vehicle = new Ambulance();
          break;
        case 'garbagetruck':
          vehicle = new GarbageTruck();
          break;
        case 'bulldozer':
          vehicle = new Bulldozer();
          break;
        case 'roadroller':
          vehicle = new RoadRoller();
          break;
        case 'excavator':
          vehicle = new Excavator();
          break;
        case 'tank':
          vehicle = new Tank();
          break;
        default:
          console.error('Unknown game ID:', gameId);
          vehicle = new Car();
      }

      engineRef.current = new VehicleGameEngine(vehicle);
      animate();
    };

    const animate = () => {
      requestAnimationFrame(animate);
      if (engineRef.current && rendererRef.current) {
        engineRef.current.update();
        rendererRef.current.render(
          engineRef.current.getScene(),
          engineRef.current.getCamera()
        );
      }
    };

    initGame();

    const container = containerRef.current;
    return () => {
      if (rendererRef.current && container) {
        container.removeChild(rendererRef.current.domElement);
        rendererRef.current.dispose();
        rendererRef.current = null;
      }
    };
  }, [gameId]);

  return (
    <div className="relative w-screen h-screen">
      <div ref={containerRef} className="w-full h-full" />
      <button
        className="absolute top-4 left-4 bg-white/30 backdrop-blur-sm px-4 py-2 rounded-lg"
        onClick={onBack}
      >
        Back to Menu
      </button>
      <GameControls
        onDirectionPress={(direction) => engineRef.current?.handleMobileControl(direction, true)}
        onDirectionRelease={() => engineRef.current?.handleMobileControl('up', false)}
      />
    </div>
  );
};

export default Game;